import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';
import useToken from './hooks/useToken';
import useUser from './hooks/useUser';
import 'moment/locale/pt-br';
import utils from '../src/helpers/FunctionsHelpers';
import sessionHelper from '../src/helpers/SessionHelpers';
import Api from './Api';

const Loading = React.lazy(() => import('./components/Loading/Loading'));

const Header = React.lazy(() => import('./components/Header/Header'));
const Dashboard = React.lazy(() => import('./components/Dashboard/Dashboard'));
const Preferences = React.lazy(() => import('./components/Preferences/Preferences'));
const Sites = React.lazy(() => import('./components/Sites/Sites'));
const Login = React.lazy(() => import('./components/Login/Login'));
const ForgotPassword = React.lazy(() => import('./components/ForgotPassword/ForgotPassword'));
const Sidebar = React.lazy(() => import('./components/Sidebar/Sidebar'));
const SiteForm = React.lazy(() => import('./components/Sites/Form'));
const SiteList = React.lazy(() => import('./components/Sites/List'));
const BuildingsList = React.lazy(() => import('./components/Sites/Buildings/List'));
const Organizations = React.lazy(() => import('./components/Organizations/Organizations'));
const OrganizationList = React.lazy(() => import('./components/Organizations/List'));
const OrganizationForm = React.lazy(() => import('./components/Organizations/Form'));
const Issues = React.lazy(() => import('./components/Issues/Issues'));
const IssuesList = React.lazy(() => import('./components/Issues/IssuesList'));
const IssuesForm = React.lazy(() => import('./components/Issues/IssuesForm'));
const IssuesReceipt = React.lazy(() => import('./components/Issues/IssuesReceipt'));
const IssuesAccording = React.lazy(() => import('./components/Issues/IssuesAccording'));
const IssuesEdit = React.lazy(() => import('./components/Issues/IssuesEdit'));
const Users = React.lazy(() => import('./components/Users/Users'));
const UsersList = React.lazy(() => import('./components/Users/UsersList'));
const UsersForm = React.lazy(() => import('./components/Users/UsersForm'));
const Videos = React.lazy(() => import('./components/Videos/Videos'));
const VideosList = React.lazy(() => import('./components/Videos/VideosList'));
const Inspections = React.lazy(() => import('./components/Inspections/Inspections'));
const InspectionsList = React.lazy(() => import('./components/Inspections/InspectionsList'));
const Iots = React.lazy(() => import('./components/Iots/Iots'));
const IotsList = React.lazy(() => import('./components/Iots/IotsList'));
const IotsMosaic = React.lazy(() => import('./components/Iots/IotsMosaic'));
const Responsibles = React.lazy(() => import('./components/Responsibles/Responsibles'));
const Compliances = React.lazy(() => import('./components/Compliances/Compliances'));
const CompliancesList = React.lazy(() => import('./components/Compliances/CompliancesList'));
const CompliancesEdit = React.lazy(() => import('./components/Compliances/CompliancesEdit'));
const CompliancesForm = React.lazy(() => import('./components/Compliances/CompliancesForm'));
const RelatMonth = React.lazy(() => import('./components/Relats/RelatMonth'));

// ORGANIZATION PANEL
const HeaderOrganization = React.lazy(() => import('./stacks/organization/components/Header'));
const MainOrganization = React.lazy(() => import('./stacks/organization/pages/Main'));
const MainSelectOrganization = React.lazy(() => import('./stacks/organization/pages/SelectOrg'));
const DashboardOrganization = React.lazy(() => import('./stacks/organization/pages/Dashboard'));
const Maintenance = React.lazy(() => import('./stacks/organization/pages/Dashboard/Maintenance'));
const Educational = React.lazy(() => import('./stacks/organization/pages/Educational'));
const Alarm = React.lazy(() => import('./stacks/organization/pages/Dashboard/Alarm'));
const AlarmDeltafire = React.lazy(() => import('./stacks/organization/pages/Dashboard/AlarmDeltafire'));
const AlarmIntelbras = React.lazy(() => import('./stacks/organization/pages/Dashboard/AlarmIntelbras'));
const Pressure = React.lazy(() => import('./stacks/organization/pages/Dashboard/Pressure'));
const Temperature = React.lazy(() => import('./stacks/organization/pages/Dashboard/Temperature'));
const ThermalCamera = React.lazy(() => import('./stacks/organization/pages/Dashboard/ThermalCamera'));

// CLIENT PANEL
const DashboardClient = React.lazy(() => import('./stacks/client/pages/Dashboard'));

const INTERVAL_ACTIVE = process.env.REACT_APP_INACTIVE_INTERVAL;
const SESSION_INTERVAL = process.env.REACT_APP_SESSION_INTERVAL;

function App() {
  const { token, setToken } = useToken(SESSION_INTERVAL);
  const { user } = useUser();
  const baseUrl = "/painel";
  const baseUrlImg = process.env.REACT_APP_URL_IMGS;

  const [confirm, setConfirm] = useState(false);
  const [themeColor, setThemeColor] = useState('');

  //Assigns the color of the organization theme if there is stylization
  const GlobalStyle = createGlobalStyle`
    .card-header,
    .modal-header {
      background: ${({ themeColor }) => themeColor ? `linear-gradient(90deg, ${themeColor} 50%, #ececec 100%)` : ''} !important;
    }

    .btn-secondaryDefault,
    .card-header.noGradient {
      background: ${({ themeColor }) => themeColor ? `${themeColor}` : ''} !important;
    }

    .logoSvgUnidade {
      background: 0px 11px 11px white !important;
    }
  `;

  useEffect(() => {
    const logoutCall = () => {
      let nameTheme = sessionStorage.getItem('clientName');
      window.location.href = nameTheme ? `${baseUrl}?theme=${nameTheme}` : baseUrl;
    }

    const ConfirmLogout = (title, message) => {
      let logout = sessionStorage.getItem('token');
      if (!logout) { logout(); }

      if (!confirm) { 
        window.removeEventListener('keydown', handleSession);
        window.removeEventListener('click', handleSession);

        sessionStorage.setItem('activeObject', JSON.stringify({ title: title, message: message}));

        sessionHelper.clearUserSessions();
        logoutCall();
        sessionStorage.removeItem('clientName');

        setConfirm(true);
      }
    };

    const handleUserState = async () => {
      if (user) {
        const userResult = await Api.getUserInfo(user.user_id);
        if (userResult && userResult.data.situation_id == 0) {
          ConfirmLogout("Usuário inativo", "Seu acesso foi revogado. Entre em contato com o time de Gestão de Contas");
        }
      }
    }

    const handleSession = () => {
      if (user) {
        const dataExp = sessionStorage.getItem('sessionExpiration');
        const expirationDate = new Date(dataExp);
        
        if (expirationDate < new Date()) {
          ConfirmLogout("Sessão expirada", "Faça o login novamente");
          return;
        }

        utils.setSessionTimeout();
      }
    }

    window.addEventListener('keydown', handleSession);
    window.addEventListener('click', handleSession);

    const intervalId = setInterval(() => {
      handleUserState();

      window.removeEventListener('keydown', handleSession);
      window.addEventListener('click', handleSession);
    }, Number(INTERVAL_ACTIVE) * 60 * 1000); // em milissegundos

    return () => {
      clearInterval(intervalId);
    };
  }, [user, token, confirm]);

  useEffect(() => {
    const verifyIsOrgProperty = async () => {
      const res = await Api.getOrganization(user.client_id);
      if (!utils.isNullOrEmpty(res.theme_color)) {
        setThemeColor(res.theme_color);
        sessionStorage.setItem('clientName', res.client_name);
      }
    };

    if (user) {
      verifyIsOrgProperty();
    }
  }, [user]);

  if (!token) {
    return (
      <React.Suspense fallback={<></>}>
        <BrowserRouter basename={baseUrl}>
          <Routes>
            <Route path="/forgotpassword" element={<ForgotPassword baseUrl={baseUrl} />} />
            <Route path="*" element={<Login setToken={setToken} baseUrl={baseUrl} />} />
          </Routes>
        </BrowserRouter>
      </React.Suspense>
    )
  }

  if (!user) {
    setTimeout(() => { window.location.reload() }, 1000);
    return (
      <React.Suspense fallback={<></>}>
        <Loading base_url={baseUrl}/>
      </React.Suspense>
    );
  }

  // Define qual stack utilizar
  // 1 - Administrador (somente Safety2u)
  // 2 - Organização
  // 3 - Cliente
  if (user.group_id == 1) {
    return (
      <div className="wrapper">
        <BrowserRouter basename={baseUrl}>
          <React.Suspense fallback={<></>}>
            <Header base_url={baseUrl} />
            <Sidebar base_url={baseUrl} />
            <Routes>
              <Route path="/" exact element={<Navigate to={'/dashboard'} />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/unity" element={<DashboardOrganization />} clientId={user.client_id} user={user} />
              <Route path="/dashboard/unity/educational" element={<Educational />} />
              <Route path="/dashboard/maintenance/:issueId" element={<Maintenance baseUrlImg={baseUrlImg} />} user={user} />
              <Route path="/dashboard/issuesAccording/:issueId" element={<IssuesAccording />} />
              <Route path="/dashboard/Alarm/:clientId" element={<Alarm />} clientId={user.client_id} />
              <Route path="/dashboard/AlarmDeltafire/:clientId" element={<AlarmDeltafire />} clientId={user.clientId} />
              <Route path="/dashboard/AlarmIntelbras/:clientId" element={<AlarmIntelbras />} clientId={user.clientId} />
              <Route path="/dashboard/Pressure/:clientId" element={<Pressure />} clientId={user.client_id} />
              <Route path="/dashboard/Temperature/:clientId" element={<Temperature />} clientId={user.client_id} />
              <Route path="/dashboard/ThermalCamera/:clientId/:cameraId" element={<ThermalCamera />} clientId={user.client_id} />
              <Route path="/preferences" element={<Preferences />} />
              <Route path="/organizations" exact element={<Organizations />}>
                <Route index element={<OrganizationList />} />
                <Route path="list" element={<OrganizationList />} />
                <Route path="panel" element={<MainOrganization />} clientId={user.client_id} user={user} />
                <Route path=":orgId" element={<OrganizationForm />} />
              </Route>
              <Route path="/sites" exact element={<Sites />}>
                <Route index element={<SiteList />} />
                <Route path="list" element={<SiteList />} />
                <Route path=":siteId" element={<SiteForm baseUrlImg={baseUrlImg}/>}>
                  <Route index element={<BuildingsList />} />
                  <Route path="buildings" element={<BuildingsList />} />
                </Route>
              </Route>
              <Route path="/issues" exact element={<Issues />}>
                <Route index element={<IssuesList />} />
                <Route path="list" element={<IssuesList />} />
                <Route path=":issueId" element={<IssuesReceipt baseUrlImg={baseUrlImg} base_url={baseUrl} />} />
                <Route path="issuesedit/:issueId" element={<IssuesEdit baseUrlImg={baseUrlImg} />} />
                <Route path="new" element={<IssuesForm />} />
              </Route>
              <Route path="/compliances" exact element={<Compliances />}>
                <Route index element={<CompliancesList />} />
                <Route path="list" element={<CompliancesList />} />
                <Route path=":issueId" element={<IssuesAccording baseUrlImg={baseUrlImg}/>} />
                <Route path="compliancesedit/:issueId" element={<CompliancesEdit baseUrlImg={baseUrlImg} />} />
                <Route path="new" element={<CompliancesForm />} />
              </Route>
              <Route path="/client">
                <Route path="dashboard" element={<DashboardOrganization baseUrl={baseUrl} user={user} preview={1} />} />
              </Route>
              <Route path="/users" element={<Users />}>
                <Route path="form" element={<UsersForm />} />
                <Route path="list" element={<UsersList />} />
              </Route>
              <Route path="/videos" element={<Videos />}>
                <Route path="list" element={<VideosList />} />
              </Route>
              <Route path="/inspections" element={<Inspections />}>
                <Route path="list" element={<InspectionsList />} />
              </Route>
              <Route path="/iots" element={<Iots />}>
                <Route path="list" element={<IotsList />} />
                <Route path="mosaic" element={<IotsMosaic />} />
              </Route>
              <Route path="/relats" element={<RelatMonth />} />
              <Route path="/dashboard/issuesReceipt/:issueId" element={<IssuesReceipt baseUrlImg={baseUrlImg} />} />
            </Routes>
            <footer id="footer" className="footer">
              <div className="copyright">
                Powered by <a href="https://safety2u.com.br/"><img src={`${baseUrl}/assets/img/logo.svg`} alt="" height="20" /></a>
                <br></br>
                <span style={{ fontSize: "10px" }}>© SAFETY2U v1.10.0 2026 | Todos os Direitos Reservados.</span>
              </div>
            </footer>
          </React.Suspense>
        </BrowserRouter>
      </div>
    );
  } else if (user.group_id == 2) {
    const clientId = user.client_ids.split(',')[0];

    let clientIdsArray = user.client_ids.split(',');

    let firstRouteElement;

    if(clientIdsArray.length > 1){
      firstRouteElement = <Route path="/" element={<MainSelectOrganization baseUrlImg={baseUrlImg} />} />;
    }else{
      firstRouteElement = <Route path="/" exact element={<Navigate to={'/main'} />} />
    }

    return (
      <div className="wrapper">
        <GlobalStyle themeColor={themeColor}/>
        <BrowserRouter basename={baseUrl}>
          <React.Suspense fallback={<></>}>
            <HeaderOrganization baseUrl={baseUrl} clientId={clientId} user={user} baseUrlImg={baseUrlImg}/>
            <Routes>
              {firstRouteElement}
              <Route path="/main" element={<MainOrganization />} clientId={clientId} user={user} />
              <Route path="/educational" element={<Educational />} />
              <Route path="/dashboard" element={<DashboardOrganization />} clientId={clientId} user={user} />
              <Route path="/dashboard/maintenance/:issueId" element={<Maintenance baseUrlImg={baseUrlImg} />} user={user}  />
              <Route path="/dashboard/Alarm/:clientId" element={<Alarm />} clientId={clientId} />
              <Route path="/dashboard/AlarmDeltafire/:clientId" element={<AlarmDeltafire />} clientId={clientId} />
              <Route path="/dashboard/AlarmIntelbras/:clientId" element={<AlarmIntelbras />} clientId={clientId} />
              <Route path="/dashboard/Pressure/:clientId" element={<Pressure />} clientId={clientId} />
              <Route path="/dashboard/Temperature/:clientId" element={<Temperature />} clientId={clientId} />
              <Route path="/dashboard/ThermalCamera/:clientId/:cameraId" element={<ThermalCamera />} clientId={clientId} />
              <Route path="/dashboard/issuesReceipt/:issueId" element={<IssuesReceipt baseUrlImg={baseUrlImg} />} />
              <Route path="/dashboard/issuesAccording/:issueId" element={<IssuesAccording />} />
              <Route path="/client">
                <Route path="dashboard" element={<DashboardClient baseUrl={baseUrl} user={user} preview={2} />} />
              </Route>
              <Route path="/iots" element={<Iots />}>
                <Route path="mosaic" element={<IotsMosaic />} />
              </Route>
            </Routes>
            <footer id="footer" className="footer">
              <div className="copyright">
                Powered by <a href="https://safety2u.com.br/"><img src={`${baseUrl}/assets/img/logo.svg`} height="20" alt="" /></a>
                <br></br>
                <span style={{ fontSize: "10px" }}>© SAFETY2U v1.10.0 2026 | Todos os Direitos Reservados.</span>
              </div>
            </footer>
          </React.Suspense>
        </BrowserRouter>
      </div>
    );
  } else if (user.group_id == 3) {
    const clientId = user.client_id;

    let firstRouteElement;
    let secRouteElement;

    if (user.qtdRespUnity > 1) {
      firstRouteElement = <Route path="/" element={<Responsibles />} />;
      secRouteElement = <Route path="/dashboard" element={<DashboardOrganization />} clientId={clientId} user={user} />;
    } else {
      firstRouteElement = <Route path="/" exact element={<Navigate to={'/dashboard'} />} />;
      secRouteElement = <Route path="/dashboard" element={<DashboardOrganization baseUrl={baseUrl} clientId={clientId} user={user} />} />;
    }

    return (
      <div className="wrapper">
        <GlobalStyle themeColor={themeColor}/>
        <BrowserRouter basename={baseUrl}>
          <React.Suspense fallback={<></>}>
            <HeaderOrganization baseUrl={baseUrl} clientId={clientId} user={user} baseUrlImg={baseUrlImg}/>
            <Routes>
              {firstRouteElement}
              <Route path="/educational" element={<Educational />} />
              {secRouteElement}
              <Route path="/dashboard/maintenance/:issueId" element={<Maintenance baseUrlImg={baseUrlImg} />} user={user} />
              <Route path="/dashboard/issuesReceipt/:issueId" element={<IssuesReceipt baseUrlImg={baseUrlImg} />} />
              <Route path="/dashboard/issuesAccording/:issueId" element={<IssuesAccording />} />
              <Route path="/dashboard/Alarm/:clientId" element={<Alarm />} clientId={clientId} />
              <Route path="/dashboard/AlarmDeltafire/:clientId" element={<AlarmDeltafire />} clientId={clientId} />
              <Route path="/dashboard/AlarmIntelbras/:clientId" element={<AlarmIntelbras />} clientId={clientId} />
              <Route path="/dashboard/Pressure/:clientId" element={<Pressure />} clientId={clientId} />
              <Route path="/dashboard/Temperature/:clientId" element={<Temperature />} clientId={clientId} />
              <Route path="/dashboard/ThermalCamera/:clientId/:cameraId" element={<ThermalCamera />} clientId={clientId} />
            </Routes>
            <footer id="footer" className="footer">
              <div className="copyright">
                Powered by <a href="https://safety2u.com.br/"><img src={`${baseUrl}/assets/img/logo.svg`} alt="" height="20" /></a>
                <br></br>
                <span style={{ fontSize: "10px" }}>© SAFETY2U v1.10.0 2026 | Todos os Direitos Reservados.</span>
              </div>
            </footer>
          </React.Suspense>
        </BrowserRouter>
      </div>
    );
  } else {
    <div className='wrapper'>
      <h2>Página não encontrada</h2>
    </div>
  }
}

export default App;